<template>
    <div class="bg-white">
        <Hero :title="$tc('jobs.search.count_label', 1)" :lead="model.title" :description="model.reference_number">
            <template #right>
                <div
                    class="
                        col-span-12
                        sm:col-span-4
                        grid grid-cols-4
                        gap-5
                        pb-18
                        flex
                        items-end
                        justify-start
                        -mt-10
                        sm:mt-0
                    "
                >
                    <Button
                        :label="$t('jobs.apply')"
                        class="
                            p-button-secondary
                            col-span-2
                            sm:col-span-4
                            md:col-span-3
                            xl:col-span-2
                            flex
                            justify-center
                        "
                        @click="$scrollToElement('#apply')"
                    />
                </div>
            </template>
        </Hero>
        <div class="text-black md:pt-8 pb-24">
            <div class="container grid grid-cols-12 gap-5">
                {{/* Right side, details */}}
                <div class="relative col-span-12 lg:col-span-4 lg:col-start-9 lg:order-2">
                    <div class="lg:pl-4 lg:border-l lg:border-gray-200 mb-10">
                        <div class="grid grid-cols-2 gap-x-5 gap-y-5 sm:gap-y-8">
                            <div v-if="model.country || model.regions.length" class="col-span-1 lg:col-span-2">
                                <div class="mb-1">{{ $t('jobs.filters.country') }}</div>
                                <div v-if="model.regions.length" class="font-bold">
                                    {{ model.regions.map((x) => x.title).join(', ') }}
                                </div>
                                <div v-if="model.country" class="font-bold">{{ model.country.name }}</div>
                            </div>

                            <div v-if="model.deadline" class="col-span-1 lg:col-span-2">
                                <div class="mb-1">{{ $t('jobs.properties.application_deadline') }}</div>
                                <div class="font-bold">
                                    {{ $dayjs(model.deadline * 1000).format('YYYY. MMMM DD.') }}
                                </div>
                            </div>

                            <div v-if="model.sector" class="col-span-1 lg:col-span-2">
                                <div class="mb-1">{{ $t('jobs.properties.fields_of_expertise') }}</div>
                                <div class="font-bold">{{ model.sector.name }}</div>
                            </div>

                            <div v-if="model.employment_type" class="col-span-1 lg:col-span-2">
                                <div class="mb-1">{{ $t('jobs.properties.type') }}</div>
                                <div class="font-bold">{{ model.employment_type.name }}</div>
                            </div>

                            <div v-if="model.position" class="col-span-1 lg:col-span-2">
                                <div class="mb-1">{{ $t('jobs.filters.position') }}</div>
                                <div class="font-bold">{{ model.position.name }}</div>
                            </div>

                            <div v-if="model.languages.length" class="col-span-1 lg:col-span-2">
                                <div class="mb-1">{{ $t('jobs.filters.languages') }}</div>
                                <div class="font-bold">{{ model.languages.map((x) => x.title).join(', ') }}</div>
                            </div>
                        </div>

                        <div class="mt-5 sm:mt-8 mb-4">{{ $t('generic.share') }}</div>
                        <ArticleShareButtonGroup :title="model.title" />
                    </div>
                </div>

                {{/* Left side, main content */}}
                <div class="col-span-12 lg:col-span-7 tinymce-content lg:order-1">
                    <div>
                        <h2 class="title title-lg">{{ $t('jobs.about_position') }}</h2>
                        <div class="-mt-2 sm:mt-0" v-html="model.body"></div>
                    </div>

                    <div v-if="model.your_tasks" class="mt-10 sm:mt-14">
                        <h2 class="title title-lg">{{ $t('jobs.tasks') }}</h2>
                        <div class="-mt-2 sm:mt-0" v-html="model.your_tasks"></div>
                    </div>

                    <div v-if="model.requirements" class="mt-10 sm:mt-14">
                        <h2 class="title title-lg">{{ $t('jobs.expectations') }}</h2>
                        <div class="-mt-2 sm:mt-0" v-html="model.requirements"></div>
                    </div>

                    <div v-if="model.advantage_if" class="mt-10 sm:mt-14">
                        <h2 class="title title-lg">{{ $t('jobs.advantage') }}</h2>
                        <div class="-mt-2 sm:mt-0" v-html="model.advantage_if"></div>
                    </div>

                    <div v-if="model.what_we_offer" class="mt-10 sm:mt-14">
                        <h2 class="title title-lg">{{ $t('jobs.what_we_offer') }}</h2>
                        <div class="-mt-2 sm:mt-0" v-html="model.what_we_offer"></div>
                    </div>

                    <div v-if="model.how_to_apply" class="mt-10 sm:mt-14">
                        <h2 class="title title-lg">{{ $t('jobs.how_to_apply') }}</h2>
                        <div class="-mt-2 sm:mt-0" v-html="model.how_to_apply"></div>
                    </div>

                    <div id="apply" class="mt-12 sm:mt-20 md:mt-24 grid grid-cols-7 gap-5">
                        <div class="relative col-span-7 lg:col-span-6">
                            <h2 class="title title-lg mb-4 sm:mb-6">{{ $t('jobs.application_form.title') }}</h2>

                            <ValidationObserver ref="form" slim>
                                <form novalidate class="col-span-6" @submit.prevent="onSubmit">
                                    <div class="grid grid-cols-6 gap-5">
                                        <div v-if="$i18n.locale !== 'hu'" class="col-span-6 sm:col-span-3">
                                            <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                                <InputText
                                                    v-model="first_name"
                                                    name="first_name"
                                                    :placeholder="$t('contact.first_name')"
                                                    :class="{ 'p-invalid': errors.length }"
                                                    type="text"
                                                />
                                                <InputError :errors="errors" class="ml-5" />
                                            </ValidationProvider>
                                        </div>
                                        <div class="col-span-6 sm:col-span-3">
                                            <ValidationProvider v-slot="{ errors }" vid="last_name" tag="div">
                                                <InputText
                                                    v-model="last_name"
                                                    name="last_name"
                                                    :placeholder="$t('contact.last_name')"
                                                    :class="{ 'p-invalid': errors.length }"
                                                    type="text"
                                                />
                                                <InputError :errors="errors" class="ml-5" />
                                            </ValidationProvider>
                                        </div>
                                        <div v-if="$i18n.locale === 'hu'" class="col-span-6 sm:col-span-3">
                                            <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                                <InputText
                                                    v-model="first_name"
                                                    name="first_name"
                                                    :placeholder="$t('contact.first_name')"
                                                    :class="{ 'p-invalid': errors.length }"
                                                    type="text"
                                                />
                                                <InputError :errors="errors" class="ml-5" />
                                            </ValidationProvider>
                                        </div>
                                        <div class="col-span-6">
                                            <ValidationProvider v-slot="{ errors }" vid="email" tag="div">
                                                <InputText
                                                    v-model="email"
                                                    name="email"
                                                    :placeholder="$t('contact.email_address')"
                                                    :class="{ 'p-invalid': errors.length }"
                                                    type="email"
                                                />
                                                <InputError :errors="errors" class="ml-5" />
                                            </ValidationProvider>
                                        </div>

                                        <div class="col-span-6">
                                            <ValidationProvider v-slot="{ errors }" vid="message" tag="div">
                                                <Textarea
                                                    v-model="message"
                                                    name="message"
                                                    rows="5"
                                                    :class="{ 'p-invalid': errors.length }"
                                                    :placeholder="$t('contact.message')"
                                                />
                                                <InputError :errors="errors" class="ml-5" />
                                            </ValidationProvider>
                                        </div>

                                        <div class="col-span-6 mb-8">
                                            <ValidationProvider v-slot="{ errors }" vid="files" tag="div">
                                                <FileUpload
                                                    ref="applicationAttachments"
                                                    :multiple="true"
                                                    accept="image/*,application/pdf,application/msword"
                                                    :max-file-size="5000000"
                                                    :choose-label="$t('jobs.application_form.file_upload.label')"
                                                    :file-limit="5"
                                                    :invalid-file-size-message="
                                                        $t('jobs.application_form.file_upload.errors.file_size')
                                                    "
                                                    :invalid-file-type-message="
                                                        $t('jobs.application_form.file_upload.errors.file_type')
                                                    "
                                                    :invalid-file-limit-message="
                                                        $t('jobs.application_form.file_upload.errors.file_limit')
                                                    "
                                                >
                                                </FileUpload>
                                                <InputError :errors="errors" class="mt-2" />
                                            </ValidationProvider>
                                        </div>

                                        <div class="col-span-6 sm:col-span-4">
                                            <ValidationProvider v-slot="{ errors }" vid="privacy" tag="div">
                                                <div class="p-field flex items-start">
                                                    <Checkbox
                                                        id="newsletter_privacy_policy_cbx"
                                                        v-model="accept_privacy_policy"
                                                        :binary="true"
                                                    />
                                                    <label for="newsletter_privacy_policy_cbx">
                                                        {{ $t('contact.accept_policy.msg1') }}
                                                        <a
                                                            :href="localePath({ name: 'privacy-policy' })"
                                                            target="_blank"
                                                            class="font-bold desktop:hover:underline"
                                                        >
                                                            {{ $t('contact.accept_policy.msg2') }}
                                                        </a>
                                                    </label>
                                                </div>

                                                <InputError :errors="errors" />
                                            </ValidationProvider>

                                            <div class="mt-5">
                                                <RecaptchaDisclaimer />
                                            </div>
                                        </div>

                                        <NuxtLink
                                            v-if="$store.state.jobs.lastSearchQuery"
                                            :to="getBackButtonPath()"
                                            class="
                                                p-component p-button p-button-secondary p-button-text
                                                mt-8
                                                sm:hidden
                                                col-span-3
                                            "
                                        >
                                            <span class="pi pi-arrow-left p-button-icon p-button-icon-left"></span>
                                            <span class="p-button-label">{{ $t('generic.back') }}</span>
                                        </NuxtLink>

                                        <div class="col-span-3 sm:col-span-2 sm:col-start-5 mt-8 sm:mt-0 text-right">
                                            <Button
                                                type="submit"
                                                :label="$t('jobs.apply')"
                                                class="p-button-primary w-full text-center flex justify-center"
                                            />
                                        </div>
                                    </div>
                                </form>
                            </ValidationObserver>

                            <Portal selector="body">
                                <div
                                    class="
                                        transition-opacity
                                        fixed
                                        top-0
                                        left-0
                                        w-full
                                        h-full
                                        z-50
                                        flex
                                        items-center
                                        justify-center
                                    "
                                    :class="[
                                        isThankYouMessageVisible
                                            ? 'opacity-1 pointer-events-all'
                                            : 'opacity-0 pointer-events-none',
                                    ]"
                                >
                                    <BgOverlay
                                        :is-open="isThankYouMessageVisible"
                                        class="z-5"
                                        :toggler-fn="closeConfirmDialog"
                                    />

                                    <div class="absolute p-5 bg-white z-10 rounded text-black max-w-xl mx-5">
                                        <p class="title title-lg mb-5">
                                            {{ $t('jobs.apply_confirmation_message.text_1') }}
                                        </p>
                                        <p class="text-15 mb-12">
                                            {{ $t('jobs.apply_confirmation_message.text_2') }}
                                            ({{ email }})
                                        </p>
                                        <Button class="p-button-primary" :label="'OK'" @click="closeConfirmDialog" />
                                    </div>
                                </div>
                            </Portal>
                        </div>
                    </div>

                    <NuxtLink
                        v-if="$store.state.jobs.lastSearchQuery"
                        :to="getBackButtonPath()"
                        class="p-component p-button p-button-secondary p-button-text mt-24 hidden sm:block"
                    >
                        <span class="pi pi-arrow-left p-button-icon p-button-icon-left"></span>
                        <span class="p-button-label">{{ $t('jobs.back_to_search') }}</span>
                    </NuxtLink>
                </div>
            </div>
        </div>

        {{/* Recommended positions */}}
        <div
            v-if="model.similar_jobs && model.similar_jobs.length"
            class="bg-gray-900 pt-12 sm:pt-16 pb-12 diagonally-rounded shadow-xl--custom relative z-10"
        >
            <div class="container px-0">
                <SectionWrapper :title="$t('job_seekers.lead')" is-carousel-wrapper>
                    <JobsCarousel :jobs="model.similar_jobs" />
                </SectionWrapper>
            </div>
        </div>

        <div
            v-if="model.similar_jobs && model.similar_jobs.length"
            class="absolute bottom-0 left-0 w-full h-32 bg-gray-800 z-0"
        ></div>
    </div>
</template>

<script>
import FileUpload from 'primevue/fileupload';
import Hero from '@/components/Heroes/Hero';
import SectionWrapper from '@/components/UI/SectionWrapper';
import JobsCarousel from '@/components/Carousels/JobsCarousel';
import ArticleShareButtonGroup from '@/components/UI/Buttons/ArticleShareButtonGroup';
import RecaptchaDisclaimer from '@/components/UI/RecaptchaDisclaimer';
import BgOverlay from '@/components/UI/BgOverlay';

export default {
    name: 'Job',
    components: {
        Hero,
        SectionWrapper,
        JobsCarousel,
        ArticleShareButtonGroup,
        FileUpload,
        RecaptchaDisclaimer,
        BgOverlay,
    },
    props: {
        model: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            first_name: '',
            last_name: '',
            email: '',
            message: '',
            accept_privacy_policy: false,

            isThankYouMessageVisible: false,
        };
    },
    async mounted() {
        const payload = this.model.related.map((relatedJob) => {
            return {
                lang_id: relatedJob.lang_id,
                path: `/job-seeker/jobs/${relatedJob.slug}`,
            };
        });

        payload.push({
            key: 'fallback',
            path: '/job-seeker/jobs',
        });

        this.$store.commit('globals/setAlternateUrlsForCurrentPage', payload);

        await this.$recaptcha.init();
    },
    beforeDestroy() {
        this.$store.commit('globals/setAlternateUrlsForCurrentPage', null);
        this.$recaptcha.destroy();
    },
    methods: {
        async onSubmit() {
            try {
                const token = await this.$recaptcha.execute('apply');

                const formData = new FormData();

                formData.append('first_name', this.first_name);
                formData.append('last_name', this.last_name);
                formData.append('message', this.message);
                formData.append('email', this.email);
                formData.append('privacy', this.accept_privacy_policy ? '1' : '0');

                this.$refs.applicationAttachments.files.forEach((file, i) => {
                    formData.append('files[]', this.$refs.applicationAttachments.files[i]);
                });

                await this.$axios
                    .$post(`/recaptcha/validate/multipart/job/apply/${this.model.id}`, formData, {
                        baseURL: '',
                        headers: {
                            'content-type': 'multipart/form-data',
                            'base-url': this.$axios.defaults.baseURL,
                            'recaptcha-token': token,
                        },
                    })
                    .then((res) => {
                        if (res.success === 1) {
                            this.isThankYouMessageVisible = true;
                        } else {
                            this.$refs.form.setErrors(res.error);
                        }
                    });
            } catch (err) {
                console.log(err.message);
            }

            await this.$recaptcha.reset();
        },
        closeConfirmDialog() {
            this.first_name = '';
            this.last_name = '';
            this.email = '';
            this.message = '';
            this.accept_privacy_policy = false;
            this.$refs.applicationAttachments.clear();

            this.$refs.form.reset();

            this.isThankYouMessageVisible = false;
        },
        getBackButtonPath() {
            return this.localePath(
                `/job-seeker/jobs?${new URLSearchParams(this.$store.state.jobs.lastSearchQuery).toString()}`
            );
        },
    },
};
</script>

<style scoped></style>
